import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Badge,
  Spinner,
  Link,
  Card,
  CardHeader,
  CardBody,
  Stack,
  HStack,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { TimeIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import GaugeChart from "react-gauge-chart";
import apiClient from "../api/apiClient";

// Utility: relative time formatting
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const seconds = Math.floor((new Date() - date) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const intervals = [
    { unit: "year", secs: 31536000 },
    { unit: "month", secs: 2592000 },
    { unit: "day", secs: 86400 },
    { unit: "hour", secs: 3600 },
    { unit: "minute", secs: 60 },
  ];

  for (const i of intervals) {
    const count = Math.floor(seconds / i.secs);
    if (count >= 1) return rtf.format(-count, i.unit);
  }
  return "just now";
}

export default function MarketAnalyzer() {
  const [news, setNews] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const linkColor = useColorModeValue("blue.600", "blue.300");

  const sourceBg = useColorModeValue("gray.100", "gray.700");
  const sourceColor = useColorModeValue("gray.600", "gray.400");

  const badgeColors = {
    bloomberg: "purple",
    reuters: "orange",
    wsj: "blue",
    cnbc: "green",
    "ft.com": "pink",
    marketwatch: "teal",
    seekingalpha: "cyan",
    businessinsider: "yellow",
  };

  useEffect(() => {
    async function fetchMarketData() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token or invalid format");

        const headers = { Authorization: `Bearer ${token}` };
        const sentRes = await apiClient.get("/market/sentiment", { headers });

        const sentimentData = sentRes.data || {};


        if (!sentimentData || Object.keys(sentimentData).length === 0)
          throw new Error("No sentiment data available");


        setSentiment(sentimentData);

      } catch (err) {
        console.error("❌ Market data fetch error:", err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchMarketData();
  }, []);

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
        <Text mt={3} color="gray.500">
          Analyzing today’s market sentiment...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={20}>
        <Text color="red.400" fontSize="lg">
          ⚠️ {error}
        </Text>
      </Box>
    );
  }

  const getGaugeColor = () => {
    switch (sentiment?.overall_sentiment?.toLowerCase()) {
      case "bullish":
        return "#48BB78";
      case "bearish":
        return "#F56565";
      default:
        return "#ECC94B";
    }
  };

  const gaugePercent = (sentiment?.sentiment_score ?? 50) / 100;

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Heading mb={6} fontSize={{ base: "2xl", md: "3xl" }}>
        📊 Market Sentiment Dashboard
      </Heading>

      {/* Half-Circle Gauge */}
      {sentiment && (
        <Box mb={6} maxW="260px" mx="auto" position="relative">
          <GaugeChart
            id="market-sentiment-gauge"
            nrOfLevels={3}
            colors={["#F56565", "#ECC94B", "#48BB78"]}
            arcWidth={0.3}
            percent={gaugePercent}
            needleColor="#333"
            needleBaseColor="#333"
            textColor="#000"
            animate={true}
            arcsLength={[0.33, 0.34, 0.33]}
          />

          <Text mt={2} textAlign="center" fontSize="lg" fontWeight="bold"

            color={getGaugeColor()}>
            Score: {sentiment.overall_sentiment?.toUpperCase() ?? "NEUTRAL"}
          </Text>
        </Box>
      )}

      {/* Market Summary Card */}
      {sentiment && sentiment.summary_sections?.length > 0 && (
        <Card bg={cardBg} shadow="lg" borderRadius="2xl" p={5} mb={8}>
          <HStack justify="space-between" flexWrap="wrap" mb={3}>
            <Badge
              colorScheme={
                sentiment.overall_sentiment?.toLowerCase() === "bullish"
                  ? "green"
                  : sentiment.overall_sentiment?.toLowerCase() === "bearish"
                    ? "red"
                    : "yellow"
              }
              px={3}
              py={1}
              borderRadius="full"
            >
              {sentiment.overall_sentiment || "Neutral"}
            </Badge>
            <Text fontSize="sm" color="gray.500">
              Updated {sentiment.updated || "recently"}
            </Text>
          </HStack>

          <Heading size="md" mb={3}>
            {sentiment.headline || "Today's Market Overview"}
          </Heading>

          {/* Dynamic Summary Sections */}
          <VStack align="start" spacing={4}>
            {sentiment.summary_sections.map((sec, idx) => (
              <Box key={idx}>
                <Text fontWeight="semibold" fontSize="lg" mb={1}>
                  {sec.subheadline}
                </Text>
                <Text fontSize="sm" color="gray.700">
                  {sec.paragraph}
                </Text>
              </Box>
            ))}
          </VStack>

          {/* {sentiment.top_sources?.length > 0 && (
            <HStack spacing={3} wrap="wrap" mt={4}>
              {sentiment.top_sources.map((src, i) => (
                <Text
                  key={i}
                  fontSize="sm"
                  color={sourceColor}
                  borderRadius="md"
                  px={2}
                  py={1}
                  bg={sourceBg}
                >
                  {src}
                </Text>
              ))}
            </HStack>
          )} */}

        </Card>
      )}


    </Box>
  );
}
